import React from 'react';
import { Form } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import * as Headless from '@headlessui/react';

import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import {
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Textarea } from '@oysterjs/ui/textarea';
import { AnimateIn } from '@oysterjs/ui/transition';

import { CaraWorkspaceUseCase, type CaraWorkspaceFileInput } from '~/api/graphql';
import { AcordDocumentPicker } from '~/components/presets/acord-document-picker';
import { emptyFileInputChange, type FileInputChange } from '~/components/file';
import { LaunchClientContext } from '~/components/launch-client';
import {
  AnimatedUploadProgress,
  extractUploadFiles
} from '~/components/cara-input/animated-upload-progress';
import { CopilotSubmitError } from '~/components/overage-limit-error';
import { type CopilotActionError } from '~/lib/copilot-action-error';
import { type CopilotWorkspaceCreateOptions } from '~/components/presets/copilot-launcher';
import { type TemplatePickerOption } from '~/components/cara-input/cara-input-template-picker-dialog';
import { RunCardDeliverable } from './run-card-deliverable';
import { RunCardFooterControls, type LinkedAccount } from './run-card-footer';
import { RunCardSources } from './run-card-sources';
import {
  buildRunCardSubmission,
  expandLanes,
  initialRepeatCount,
  initialRunCardState,
  runCardBlockedReason,
  visibleFields,
  type RunCardState,
  type RunCardTemplate,
  type RunCardTemplateKind
} from './run-card-submission';
import {
  acordDocumentsForScope,
  hasRequiredLibrary,
  familyForPreset,
  useCaseById,
  useCasesInFamily,
  type ExtraFieldSpec,
  type UseCaseSpec
} from './use-case-catalog';
import { useEffortLevel } from './use-effort-level';
import { EffortLevelSelect } from '~/components/presets/effort-level-select';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { VisibilitySelectorValue } from '~/components/visibility/visibility-selector.utils';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useTemplateLimit } from '~/components/presets/template-limit';

export type RunCardCreateWorkspace = (
  query: string,
  files: CaraWorkspaceFileInput[],
  options?: CopilotWorkspaceCreateOptions
) => Promise<void>;

let laneKeySeq = 0;
const nextLaneKey = () => `lane-${(laneKeySeq += 1)}`;

const ExtraFieldControl = ({
  field,
  value,
  onChange
}: {
  field: ExtraFieldSpec;
  value: string;
  onChange: (next: string) => void;
}) => {
  if (field.choices) {
    return (
      <Select name={field.key} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        {field.choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </Select>
    );
  }
  if (field.multiline) {
    return (
      <Textarea
        name={field.key}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    );
  }
  return <Input name={field.key} value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
};

type RunCardProps = {
  useCaseId: CaraWorkspaceUseCase;
  initialDeliverableKey?: string;
  onClose: () => void;
  onChangeUseCase: (next: CaraWorkspaceUseCase) => void;
  createWorkspace: RunCardCreateWorkspace;
  error?: CopilotActionError;
  hasProAccess: boolean;
  libraries: Partial<Record<RunCardTemplateKind, TemplatePickerOption[]>>;
  /** Preselects the linked account, for a card opened from that client. */
  initialAccount?: LinkedAccount;
};

/**
 * Mount inside an `AnimatePresence`. Open by construction, so the `open`-driven
 * dialog cannot animate in from the same commit that creates it. Keyed on the
 * use case so Task switches swap fields without remounting the dialog.
 */
export const UseCaseRunCard = ({ useCaseId, onClose, ...rest }: RunCardProps) => {
  const spec = useCaseById(useCaseId);
  if (!spec) {
    return null;
  }

  return (
    <MountableDialog onClose={onClose} size="2xl">
      <RunCardForm key={spec.id} spec={spec} onClose={onClose} {...rest} />
    </MountableDialog>
  );
};

const RunCardForm = ({
  spec,
  initialDeliverableKey,
  initialAccount,
  ...props
}: { spec: UseCaseSpec } & Omit<RunCardProps, 'useCaseId'>) => {
  const defaultVisibility = useDefaultVisibilityValue();
  const visibilityEnabled = useVisibilityEntitlement();

  const initialRepeat = initialRepeatCount(spec);

  const [loading, setLoading] = React.useState(false);
  const [repeatCount, setRepeatCount] = React.useState(initialRepeat);
  const [laneKeys, setLaneKeys] = React.useState<string[]>(() =>
    expandLanes(spec, initialRepeat).map(() => nextLaneKey())
  );
  const [state, setState] = React.useState<RunCardState>(() =>
    initialRunCardState(spec, initialRepeat, initialDeliverableKey)
  );
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [account, setAccount] = React.useState<LinkedAccount | undefined>(initialAccount);
  const launchClient = React.useMemo(
    () => (account ? { id: account.id, name: account.name } : null),
    [account]
  );
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);

  const family = familyForPreset(spec.preset);
  // Same library gate as the tray; keep the current task listed so the control cannot lose its selection.
  const siblings = useCasesInFamily(spec.preset).filter(
    (sibling) => sibling.id === spec.id || hasRequiredLibrary(sibling, props.libraries)
  );
  const fields = visibleFields(spec, state.documentIds);
  const documentOptions = spec.documents ? acordDocumentsForScope(spec.documents.scope) : [];
  const documentLimit = useTemplateLimit();

  const {
    effortLevel,
    selectorEnabled: effortSelectorEnabled,
    choices: effortChoices
  } = useEffortLevel(state);
  const hasMoreOptions = (spec.options?.length ?? 0) > 0 || !!spec.scope;

  // The hook drops a level this user cannot run.
  const effectiveState = { ...state, effortLevel };
  const blockedReason = runCardBlockedReason(spec, effectiveState, repeatCount);

  const patch = (next: Partial<RunCardState>) => setState((prev) => ({ ...prev, ...next }));

  const setLane = (index: number, change: FileInputChange) =>
    setState((prev) => ({
      ...prev,
      lanes: prev.lanes.map((lane, i) => (i === index ? change : lane))
    }));

  const addLane = () => {
    setRepeatCount((prev) => prev + 1);
    setState((prev) => ({ ...prev, lanes: [...prev.lanes, emptyFileInputChange()] }));
    setLaneKeys((prev) => [...prev, nextLaneKey()]);
  };

  const removeLane = (index: number) => {
    setRepeatCount((prev) => Math.max(1, prev - 1));
    setState((prev) => ({ ...prev, lanes: prev.lanes.filter((_, i) => i !== index) }));
    setLaneKeys((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    const submission = buildRunCardSubmission(spec, effectiveState, repeatCount);
    setLoading(true);
    try {
      await props.createWorkspace(submission.message, submission.files, {
        ...submission.options,
        visibility,
        clientId: account?.id
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LaunchClientContext.Provider value={launchClient}>
      <div className="min-w-0">
        <DialogTitle>{spec.title}</DialogTitle>
        {/* The tile has already sold the task; a reader who opened the card wants the
            bounds, so this takes the summary rather than repeating the one-liner. */}
        <DialogDescription>{spec.about.summary}</DialogDescription>
      </div>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        // Search fields inside the form; only the button starts a run.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
      >
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {/* A family with one run has nothing to switch to, and the dialog
                  title already names it. */}
              {siblings.length > 1 && (
                <Field>
                  <Label>Task</Label>
                  {family && <Description>{family.description}</Description>}
                  <Select
                    value={spec.id}
                    onChange={(e) =>
                      props.onChangeUseCase(e.currentTarget.value as CaraWorkspaceUseCase)
                    }
                  >
                    {siblings.map((sibling) => (
                      <option
                        key={sibling.id}
                        value={sibling.id}
                        disabled={sibling.pro && !props.hasProAccess}
                      >
                        {sibling.title}
                        {sibling.pro && !props.hasProAccess ? ' (Pro)' : ''}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {spec.documents && (
                <Field>
                  <Label>{spec.documents.label}</Label>
                  <Description>
                    {typeof spec.documents.description === 'function'
                      ? spec.documents.description(documentLimit)
                      : spec.documents.description}
                  </Description>
                  <AcordDocumentPicker
                    options={documentOptions}
                    value={state.documentIds}
                    placeholder={spec.documents.placeholder}
                    maxSelections={documentLimit.count}
                    onChange={(documentIds) => patch({ documentIds })}
                  />
                </Field>
              )}

              <RunCardSources
                spec={spec}
                repeatCount={repeatCount}
                laneKeys={laneKeys}
                onChangeLane={setLane}
                onAddLane={addLane}
                onRemoveLane={removeLane}
              />

              {fields.filter((field) => field.half).length > 0 && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {fields
                    .filter((field) => field.half)
                    .map((field) => (
                      <Field key={field.key}>
                        <Label>{field.label}</Label>
                        {field.description && <Description>{field.description}</Description>}
                        <ExtraFieldControl
                          field={field}
                          value={state.fields[field.key] ?? ''}
                          onChange={(next) =>
                            patch({ fields: { ...state.fields, [field.key]: next } })
                          }
                        />
                      </Field>
                    ))}
                </div>
              )}

              {fields
                .filter((field) => !field.half)
                .map((field) => (
                  <Field key={field.key}>
                    <Label>{field.label}</Label>
                    {field.description && <Description>{field.description}</Description>}
                    <ExtraFieldControl
                      field={field}
                      value={state.fields[field.key] ?? ''}
                      onChange={(next) => patch({ fields: { ...state.fields, [field.key]: next } })}
                    />
                  </Field>
                ))}

              <RunCardDeliverable
                spec={spec}
                selectedKey={state.deliverableKey}
                onSelectKey={(key) => patch({ deliverableKey: key, template: undefined })}
                libraries={props.libraries}
                template={state.template}
                onSelectTemplate={(template: RunCardTemplate) => patch({ template })}
              />

              <Field>
                <Label>{spec.instructions.label}</Label>
                <Textarea
                  name="instructions"
                  rows={3}
                  placeholder={spec.instructions.placeholder}
                  value={state.instructions}
                  onChange={(e) => patch({ instructions: e.currentTarget.value })}
                />
              </Field>

              {hasMoreOptions && (
                <div className="flex flex-col gap-6">
                  <Headless.Button
                    type="button"
                    onClick={() => setMoreOpen((prev) => !prev)}
                    aria-expanded={moreOpen}
                    className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    More options
                    <ChevronDownIcon className={moreOpen ? 'size-4 rotate-180' : 'size-4'} />
                  </Headless.Button>
                  {moreOpen && (
                    <>
                      {(spec.options ?? []).map((option) => (
                        <SwitchField key={option.key}>
                          <Label>{option.label}</Label>
                          <Description>{option.description}</Description>
                          <Switch
                            color="sky"
                            checked={state.options[option.key] ?? option.defaultOn}
                            onChange={(checked) =>
                              patch({ options: { ...state.options, [option.key]: checked } })
                            }
                          />
                        </SwitchField>
                      ))}
                      {spec.scope && (
                        <Field>
                          <Label>{spec.scope.label}</Label>
                          <Description>{spec.scope.description}</Description>
                          <CheckboxGroup>
                            {spec.scope.options.map((option) => (
                              <CheckboxField key={option.value}>
                                <Checkbox
                                  color="sky"
                                  checked={state.scope.includes(option.value)}
                                  onChange={(checked) =>
                                    patch({
                                      scope: checked
                                        ? [...state.scope, option.value]
                                        : state.scope.filter((v) => v !== option.value)
                                    })
                                  }
                                />
                                <Label>{option.label}</Label>
                              </CheckboxField>
                            ))}
                          </CheckboxGroup>
                        </Field>
                      )}
                    </>
                  )}
                </div>
              )}
            </FieldGroup>
          </Fieldset>
          <AnimatePresence>
            {props.error && (
              <AnimateIn>
                <CopilotSubmitError className="mt-4" error={props.error} />
              </AnimateIn>
            )}
            {loading && (
              <AnimateIn>
                <AnimatedUploadProgress
                  files={extractUploadFiles(state.lanes.flatMap((lane) => lane.files))}
                />
              </AnimateIn>
            )}
          </AnimatePresence>
        </DialogBody>
        <DialogFooter
          actions={
            <>
              <Button type="button" color="white" disabled={loading} onClick={props.onClose}>
                Close
              </Button>
              {effortSelectorEnabled && effortLevel && (
                <EffortLevelSelect
                  value={effortLevel}
                  onChange={(level) => patch({ effortLevel: level })}
                  choices={effortChoices}
                  disabled={loading}
                />
              )}
              <WorkspaceCreateSubmitButton
                loading={loading}
                disabled={!!blockedReason}
                title={blockedReason}
                visibilityEnabled={visibilityEnabled}
                visibility={visibility}
                onVisibilityChange={setVisibility}
              />
            </>
          }
        >
          <RunCardFooterControls account={account} onChangeAccount={setAccount} />
        </DialogFooter>
      </Form>
    </LaunchClientContext.Provider>
  );
};

const WorkspaceCreateSubmitButton = (props: {
  loading?: boolean;
  disabled?: boolean;
  /** Why submit is disabled, as a tooltip. */
  title?: string;
  visibilityEnabled?: boolean;
  visibility: VisibilitySelectorValue;
  onVisibilityChange: (next: VisibilitySelectorValue) => void;
}) => {
  if (!props.visibilityEnabled) {
    return (
      <Button
        type="submit"
        color="sky"
        loading={props.loading}
        disabled={props.disabled}
        title={props.title}
      >
        Create Workspace
      </Button>
    );
  }

  return (
    <VisibilitySplitButton
      type="submit"
      value={props.visibility}
      onChange={props.onVisibilityChange}
      loading={props.loading}
      disabled={props.disabled}
      title={props.title}
    >
      Create Workspace
    </VisibilitySplitButton>
  );
};
